import React, { useEffect, useRef, useState } from 'react';
import api from '../api/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBell, faCheck, faCheckDouble,
    faTriangleExclamation, faCircleExclamation, faArrowsRotate,
} from '@fortawesome/free-solid-svg-icons';
import '../notification-bell.css';

const SEVERITY_ICON = {
    critical: faTriangleExclamation,
    warning: faCircleExclamation,
};

function NotificationBell() {
    const [departments, setDepartments] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    const fetchNotifications = () => {
        api.get('/api/notifications')
            .then(r => {
                setDepartments(r.data.departments || []);
                setUnreadCount(r.data.unreadCount || 0);
            })
            .catch(() => {});
    };

    useEffect(() => { fetchNotifications(); }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const closePanel = () => setOpen(false);
        window.addEventListener('header:closeMobileMenu', closePanel);
        return () => window.removeEventListener('header:closeMobileMenu', closePanel);
    }, []);

    const toggleOpen = () => {
        if (!open) fetchNotifications();
        setOpen(o => !o);
    };

    const markAsRead = (notification) => {
        api.post(`/api/notifications/${encodeURIComponent(notification.id)}/read`, {
            signature: notification.signature,
        }).then(fetchNotifications).catch(() => {});
    };

    const markAllAsRead = () => {
        const unread = departments.flatMap(d => d.notifications.filter(n => !n.read));
        if (unread.length === 0) return;
        api.post('/api/notifications/read-all', {
            items: unread.map(n => ({ id: n.id, signature: n.signature })),
        }).then(fetchNotifications).catch(() => {});
    };

    // Кнопка-стрелочка в уведомлении: дергает серверный синк (например,
    // /api/sync-day-gaps — фоновый, отвечает сразу). Статус на иконке:
    // крутится = запущено, галочка = принято, восклицательный = ошибка/уже идёт.
    const [actionState, setActionState] = useState({});
    const runAction = (n) => {
        if (actionState[n.id] === 'running') return;
        setActionState(s => ({ ...s, [n.id]: 'running' }));
        api.post(n.action.endpoint, {})
            .then(() => {
                setActionState(s => ({ ...s, [n.id]: 'done' }));
                setTimeout(fetchNotifications, 2000);
            })
            .catch(() => setActionState(s => ({ ...s, [n.id]: 'error' })));
    };

    const handleNotificationClick = (notification) => {
        if (!notification.read) markAsRead(notification);
        if (notification.link) window.location.href = notification.link;
    };

    const visibleDepartments = departments.filter(d => d.notifications.length > 0);

    return (
        <div className="notification-bell" ref={containerRef}>
            <button type="button" className="notification-bell__trigger" onClick={toggleOpen}>
                <FontAwesomeIcon icon={faBell} />
                <span className="notification-bell__label">Notifications</span>
                {unreadCount > 0 && (
                    <span className="notification-bell__badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
            </button>

            {open && (
                <div className="notification-bell__panel">
                    <div className="notification-bell__panel-header">
                        <span>Notifications</span>
                        {unreadCount > 0 && (
                            <button type="button" className="notification-bell__mark-all" onClick={markAllAsRead}>
                                <FontAwesomeIcon icon={faCheckDouble} />
                                Mark all as read
                            </button>
                        )}
                    </div>

                    <div className="notification-bell__panel-body">
                        {visibleDepartments.length === 0 ? (
                            <p className="notification-bell__empty">No notifications.</p>
                        ) : (
                            visibleDepartments.map(dept => (
                                <div key={dept.department} className="notification-bell__department">
                                    <div className="notification-bell__department-label">{dept.label}</div>
                                    {dept.notifications.map(n => (
                                        <div
                                            key={n.id}
                                            className={`notification-bell__item notification-bell__item--${n.severity} ${n.read ? 'is-read' : ''}`}
                                            onClick={() => handleNotificationClick(n)}
                                        >
                                            <FontAwesomeIcon
                                                icon={SEVERITY_ICON[n.severity] || faCircleExclamation}
                                                className="notification-bell__item-icon"
                                            />
                                            <div className="notification-bell__item-text">
                                                <div className="notification-bell__item-title">{n.title}</div>
                                                <div className="notification-bell__item-message">{n.message}</div>
                                            </div>
                                            {n.action && (
                                                <button
                                                    type="button"
                                                    className={`notification-bell__item-action-btn is-${actionState[n.id] || 'idle'}`}
                                                    title={actionState[n.id] === 'done' ? 'Sync started — refresh in a few minutes'
                                                        : actionState[n.id] === 'error' ? 'Failed to start (maybe already running) — try again later'
                                                        : n.action.label}
                                                    onClick={(e) => { e.stopPropagation(); runAction(n); }}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={actionState[n.id] === 'done' ? faCheck
                                                            : actionState[n.id] === 'error' ? faCircleExclamation
                                                            : faArrowsRotate}
                                                        spin={actionState[n.id] === 'running'}
                                                    />
                                                </button>
                                            )}
                                            {!n.read && (
                                                <button
                                                    type="button"
                                                    className="notification-bell__item-read-btn"
                                                    title="Mark as read"
                                                    onClick={(e) => { e.stopPropagation(); markAsRead(n); }}
                                                >
                                                    <FontAwesomeIcon icon={faCheck} />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default NotificationBell;
